import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Navigate } from '@sorare/routing';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_MARKET_WATCHLISTS } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import {
  sportAnalyticTag,
  useSportContext,
} from '@sorare/core/src/contexts/sport';
import { scoutingCenter } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { IosHeader } from '#src/components/market/IosHeader/index.tsx';
import { WatchlistEmptyState } from '#src/components/watchlists/WatchlistEmptyState/index.tsx';
import { WatchlistHeader } from '#src/components/watchlists/WatchlistHeader/index.tsx';
import {
  SCOUTING_CENTER_CONFIG_BY_SPORT,
  SCOUTING_CENTER_OWNED_CARDS_RARITIES,
} from '#src/lib/scoutingCenterFilters.ts';
import { AdvancedWatchlistSearch } from '#src/searchCommonPlayer/AdvancedPlayerSearch/AdvancedWatchlistSearch/index.tsx';

import type { WatchlistPage_watchlist } from './__generated__/index.graphql.ts';

type Props = {
  watchlist: Nullable<WatchlistPage_watchlist>;
};
export const WatchlistPage = ({ watchlist }: Props) => {
  const { isMe } = useCurrentUserContext();
  const { sport: sportContext } = useSportContext();
  if (!watchlist)
    return (
      <Navigate
        to={generateSportPath(ANY_SPORT_MARKET_WATCHLISTS, {
          sport: sportContext,
        })}
      />
    );

  const { sport } = watchlist;

  const { sorts, filters } = SCOUTING_CENTER_CONFIG_BY_SPORT[sport];
  return (
    <>
      <PageHeader text={scoutingCenter.watchlists} />

      <Vertical gap={0}>
        <IosHeader text={scoutingCenter.watchlists} />
        <WatchlistHeader watchlist={watchlist} />
      </Vertical>
      {watchlist.totalPlayersCount === 0 ? (
        <WatchlistEmptyState />
      ) : (
        <AdvancedWatchlistSearch
          sport={sport}
          key={watchlist.id}
          sorts={sorts}
          defaultSort={sorts[0]}
          cardFilters={filters}
          analyticsTags={['Watchlist', sportAnalyticTag[sport]]}
          topic={{ type: 'watchlist', label: '' }}
          attributesToRetrieve={['slug']}
          watchlistId={watchlist.id}
          lastOwnedCardsRarities={SCOUTING_CENTER_OWNED_CARDS_RARITIES}
          canManageWatchlist={isMe(watchlist.user)}
        />
      )}
    </>
  );
};

WatchlistPage.fragments = {
  watchlist: gql`
    fragment WatchlistPage_watchlist on Watchlist {
      slug
      id
      sport
      user {
        slug
      }
      totalPlayersCount
      ...WatchlistHeader_watchlist
    }
    ${WatchlistHeader.fragments.watchlist}
  ` as TypedDocumentNode<WatchlistPage_watchlist>,
};
